import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FolderKanban, ArrowLeft, Save, Trash2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";

const TYPES = ["TFC", "Mémoire", "Thèse", "Article", "Étude", "Rapport", "Projet tutoré", "Autre"];
const FIELDS = [
  ["subject", "Sujet"], ["problematic", "Problématique"], ["questions", "Questions de recherche"],
  ["objectives", "Objectifs"], ["hypotheses", "Hypothèses"], ["concepts", "Concepts clés"],
  ["theoretical_framework", "Cadre théorique"], ["methodology", "Méthodologie"], ["literature_review", "Revue de littérature"],
];

export default function Projects() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState({ title: "", type: "Mémoire" });
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (projectId) api.get(`/projects/${projectId}`).then((r) => setCurrent(r.data)).catch(() => navigate("/app/projects"));
    else setCurrent(null);
  }, [projectId]);

  const create = async () => {
    if (!form.title.trim()) return;
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Projet créé");
      setOpen(false); setForm({ title: "", type: "Mémoire" }); setParams({});
      navigate(`/app/projects/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${current.id}`, current);
      setCurrent(data); toast.success("Projet enregistré");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    await api.delete(`/projects/${current.id}`);
    toast.success("Projet supprimé"); navigate("/app/projects");
  };

  // DETAIL VIEW
  if (projectId && current) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-up">
        <button onClick={() => navigate("/app/projects")} className="text-sm text-muted2 hover:text-navy flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Mes projets</button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-mono uppercase tracking-wide bg-navy/5 text-navy px-2 py-0.5 rounded">{current.type}</span>
            <Input value={current.title} onChange={(e) => setCurrent({ ...current, title: e.target.value })} className="font-serif text-2xl font-bold text-navy border-0 px-0 mt-2 h-auto focus-visible:ring-0" data-testid="project-title-input" />
            <div className="flex items-center gap-3 mt-2">
              <Progress value={current.progress} className="h-2 w-40" />
              <span className="text-sm text-muted2">{current.progress}% complété</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate(`/app/chat?project=${current.id}`)} variant="outline" className="rounded-full gap-2 border-navy text-navy" data-testid="project-chat-btn"><MessageSquare className="h-4 w-4" /> Discuter</Button>
            <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-800 rounded-full gap-2" data-testid="project-save-btn"><Save className="h-4 w-4" /> Enregistrer</Button>
          </div>
        </div>
        <div className="mt-6 space-y-4">
          {FIELDS.map(([key, label]) => (
            <div key={key} className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <Label className="text-navy font-semibold">{label}</Label>
              <Textarea value={current[key] || ""} onChange={(e) => setCurrent({ ...current, [key]: e.target.value })} rows={key === "literature_review" ? 5 : 3} className="mt-2 border-[#E5E7EB] resize-none" placeholder={`Renseignez ${label.toLowerCase()}…`} data-testid={`project-field-${key}`} />
            </div>
          ))}
          <button onClick={remove} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1.5 mt-4" data-testid="project-delete-btn"><Trash2 className="h-4 w-4" /> Supprimer ce projet</button>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="max-w-5xl mx-auto px-6 py-8 animate-fade-up">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl font-bold text-navy">Mes projets</h1><p className="text-muted2 mt-1">Chaque projet conserve sa mémoire et son historique.</p></div>
        <Button onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-800 rounded-full gap-2" data-testid="projects-new-btn"><Plus className="h-4 w-4" /> Nouveau projet</Button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-[#E5E7EB] rounded-2xl mt-8">
          <FolderKanban className="h-10 w-10 text-muted2 mx-auto" />
          <p className="text-muted2 mt-3">Créez votre premier projet de recherche.</p>
          <Button onClick={() => setOpen(true)} className="mt-4 bg-navy text-white hover:bg-navy-800 rounded-full">Commencer</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          {projects.map((p) => (
            <button key={p.id} onClick={() => navigate(`/app/projects/${p.id}`)} className="text-left bg-white rounded-2xl border border-[#E5E7EB] p-5 hover:border-navy hover:shadow-md transition-all" data-testid={`project-card-${p.id}`}>
              <span className="text-[10px] font-mono uppercase tracking-wide bg-navy/5 text-navy px-2 py-0.5 rounded">{p.type}</span>
              <h3 className="font-semibold text-navy text-lg mt-2 truncate">{p.title}</h3>
              <p className="text-sm text-muted2 mt-1 line-clamp-2">{p.subject || "Sujet non défini"}</p>
              <div className="flex items-center gap-2 mt-4"><Progress value={p.progress} className="h-1.5 flex-1" /><span className="text-xs text-muted2">{p.progress}%</span></div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setParams({}); }}>
        <DialogContent data-testid="new-project-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Nouveau projet</DialogTitle><DialogDescription>Donnez un titre et choisissez le type de travail scientifique.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Titre du projet</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="Ex. Usages des réseaux sociaux chez les étudiants" data-testid="new-project-title" /></div>
            <div><Label>Type de travail</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="new-project-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={create} className="bg-navy text-white hover:bg-navy-800 rounded-full w-full" data-testid="new-project-submit">Créer le projet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
